(() => {
  'use strict';
  const keys = ['colors', 'stock', 'sizes', 'designs', 'shapes', 'purposes'];
  const params = { colors: 'nail_color', stock: 'nail_stock', sizes: 'nail_size', designs: 'nail_design', shapes: 'nail_shape', purposes: 'nail_type' };
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  class NailFilters {
    constructor(section) {
      this.section = section;
      this.aside = section.querySelector('[data-nail-filters]');
      this.results = section.querySelector('[data-vna-collection-results]');
      this.chips = section.querySelector('[data-nail-active-filters]');
      this.status = section.querySelector('[data-nail-filter-status]');
      this.counter = section.querySelector('[data-vna-product-count]');
      this.trigger = section.querySelector('[data-nail-filter-open]');
      this.triggerCount = this.trigger?.querySelector('[data-nail-filter-count]');
      this.backdrop = section.querySelector('.nail-filter-backdrop');
      this.pageSize = Number(section.dataset.nailPageSize) || 24;
      this.ja = document.documentElement.lang.toLowerCase().startsWith('ja');
      this.text = this.ja ? {
        empty: '商品が見つかりません。', noOptions: '該当する選択肢はありません。', clear: 'すべてクリア',
        available: '在庫あり', unavailable: '在庫切れ', loading: '読み込み中…', failure: '読み込めませんでした。',
        retry: '再試行', products: '点の商品', remove: '解除', previous: '前のページ', next: '次のページ', show: '商品を見る', hand: 'ハンドネイル', toe: 'フットネイル'
      } : {
        empty: 'No products found.', noOptions: 'No options available.', clear: 'Clear all',
        available: 'In stock', unavailable: 'Sold out', loading: 'Loading…', failure: 'Could not load filters.',
        retry: 'Retry', products: 'products', remove: 'Remove', previous: 'Previous page', next: 'Next page', show: 'Show products', hand: 'Hand nail tips', toe: 'Toe nail tips'
      };
      this.cards = new Map();
      this.products = [];
      this.options = {};
      this.ready = false;
      this.revision = 0;
      this.abort = new AbortController();
      this.readState();
      this.bind();
      this.setupSticky();
      this.load();
    }

    setupSticky() {
      const signal = this.abort.signal;
      this.stickyY = Math.max(0, window.scrollY);
      this.stickyTop = null;
      this.stickyFrame = 0;
      this.stickyBars = [this.section.querySelector('.vna-category-nav-wrap'), ...document.querySelectorAll('.section-header-desktop, .section-header-mobile, [data-sticky-header], #shopify-section-header')].filter(Boolean);
      const schedule = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => { this.stickyFrame = 0; this.updateSticky(); });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      this.stickyObserver = new ResizeObserver(schedule);
      [this.aside, this.section.querySelector('.nail-catalog-layout'), ...this.stickyBars].filter(Boolean).forEach((el) => this.stickyObserver.observe(el));
      this.updateSticky();
    }

    updateSticky() {
      const y = Math.max(0, window.scrollY);
      const delta = y - this.stickyY;
      this.stickyY = y;
      if (window.innerWidth < 990) {
        this.stickyTop = null;
        this.aside.style.removeProperty('--nail-sticky-top');
        return;
      }
      let blockedTop = 0;
      this.stickyBars.forEach((bar) => {
        const style = getComputedStyle(bar);
        const rect = bar.getBoundingClientRect();
        const top = Number.parseFloat(style.top) || 0;
        if ((style.position === 'sticky' || style.position === 'fixed') && rect.height && rect.top <= top + 1 && rect.bottom > 0) blockedTop = Math.max(blockedTop, rect.bottom);
      });
      const upper = blockedTop + 20;
      const lower = Math.min(upper, window.innerHeight - this.aside.offsetHeight - 20);
      // A tall sidebar travels with the page in either direction, stopping at its bottom or top.
      // CSS sticky keeps the element inside the product area, including the footer boundary.
      this.stickyTop = Math.max(lower, Math.min(upper, (this.stickyTop ?? upper) - delta));
      this.aside.style.setProperty('--nail-sticky-top', `${Math.round(this.stickyTop)}px`);
    }

    readState() {
      const url = new URL(window.location.href);
      this.selected = Object.fromEntries(keys.map((key) => [key, new Set(url.searchParams.getAll(params[key]))]));
      this.page = Math.max(1, Number.parseInt(url.searchParams.get('nail_page') || url.searchParams.get('page') || '1', 10) || 1);
    }

    bind() {
      const signal = this.abort.signal;
      this.section.addEventListener('change', (event) => {
        const input = event.target.closest('[data-nail-filter-value]');
        if (!input || !this.ready) return;
        const set = this.selected[input.dataset.nailFilterValue];
        input.checked ? set.add(input.value) : set.delete(input.value);
        this.page = 1;
        this.render(true);
      }, { signal });
      this.aside.querySelector('form').addEventListener('submit', (event) => event.preventDefault(), { signal });
      this.section.addEventListener('click', (event) => {
        const target = event.target.closest('button, a');
        if (!target) return;
        if (target.hasAttribute('data-nail-filter-open')) this.open();
        if (target.hasAttribute('data-nail-filter-close') || target.hasAttribute('data-nail-filter-apply')) this.close();
        if (target.hasAttribute('data-nail-filter-retry')) this.load();
        if (target.hasAttribute('data-nail-clear')) {
          keys.forEach((key) => this.selected[key].clear());
          this.page = 1;
          this.render(true);
        }
        if (target.hasAttribute('data-nail-remove')) {
          this.selected[target.dataset.nailRemove].delete(target.dataset.value);
          this.page = 1;
          this.render(true);
          this.chips.querySelector('button')?.focus();
        }
        if (target.hasAttribute('data-nail-page')) {
          event.preventDefault();
          this.page = Number(target.dataset.nailPage);
          this.render(true);
          this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, { signal });
      window.addEventListener('popstate', () => {
        this.readState();
        if (this.ready) this.render(false);
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (!this.aside.classList.contains('is-open')) return;
        if (event.key === 'Escape') this.close();
        if (event.key !== 'Tab') return;
        const visible = Array.from(this.aside.querySelectorAll('button:not([disabled]), input:not([disabled]), summary')).filter((el) => el.getClientRects().length);
        const first = visible[0];
        const last = visible[visible.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }, { signal });
      window.addEventListener('resize', () => { if (window.innerWidth >= 990) this.close(false); }, { signal });
    }

    async load() {
      if (this.loading) return;
      this.loading = true;
      this.status.textContent = this.text.loading;
      this.aside.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(window.location.href);
        keys.forEach((key) => url.searchParams.delete(params[key]));
        url.searchParams.delete('nail_page');
        url.searchParams.delete('page');
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', 'nail-series-index');
        url.searchParams.set('sort_by', this.section.querySelector('[name="sort_by"]').value);
        const loadPage = async (page) => {
          const pageUrl = new URL(url);
          pageUrl.searchParams.set('page', page);
          const response = await fetch(pageUrl, { credentials: 'same-origin', signal: this.abort.signal });
          if (!response.ok) throw new Error(this.text.failure);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = doc.querySelector('[data-nail-filter-index]');
          const template = doc.querySelector('[data-nail-filter-cards]');
          if (!data || !template) throw new Error(this.text.failure);
          return { data: JSON.parse(data.textContent), template };
        };
        const first = await loadPage(1);
        const pages = [first];
        // Every page is indexed; no first-page-only filtering or fixed product cap.
        for (let page = 2; page <= first.data.pages; page++) pages.push(await loadPage(page));
        this.products = pages.flatMap((page) => page.data.products);
        this.products.forEach((product) => { product.purposes ||= []; });
        this.cards.clear();
        pages.forEach(({ template }) => {
          template.content.querySelectorAll('[data-vna-product-card]').forEach((card) => {
            if (this.section.dataset.nailQuickAdd !== 'true') card.querySelector('.nail-product-action, .vna-quick-add')?.remove();
            this.cards.set(card.dataset.productId, card.outerHTML);
          });
        });
        if (this.products.some((product) => !this.cards.has(product.id))) throw new Error(this.text.failure);
        this.makeOptions();
        this.ready = true;
        this.section.dataset.nailReady = 'true';
        this.status.textContent = '';
        this.render(false);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.innerHTML = `${escape(this.text.failure)} <button type="button" data-nail-filter-retry>${escape(this.text.retry)}</button>`;
        this.aside.querySelectorAll('[data-nail-options]').forEach((el) => { el.textContent = this.text.failure; });
        console.error('Nail series filters:', error);
      } finally {
        this.loading = false;
        this.aside.removeAttribute('aria-busy');
      }
    }

    makeOptions() {
      const labels = {
        'purple': ['パープル', 'Purple'], '粉红色': ['ピンク', 'Pink'], 'pink': ['ピンク', 'Pink'],
        '透明': ['クリア', 'Clear'], 'clear': ['クリア', 'Clear'], 'floral': ['フローラル', 'Floral'],
        '纯色': ['ワンカラー', 'Solid color'], 'solid': ['ワンカラー', 'Solid color'],
        'geometric': ['ジオメトリック', 'Geometric'], '方形': ['スクエア', 'Square'],
        'square': ['スクエア', 'Square'], 'oval': ['オーバル', 'Oval'], 'almond': ['アーモンド', 'Almond'],
        '白色': ['ホワイト', 'White'], 'white': ['ホワイト', 'White'], 'black': ['ブラック', 'Black'], '黑色': ['ブラック', 'Black'],
        'blue': ['ブルー', 'Blue'], 'beige': ['ベージュ', 'Beige'], 'brown': ['ブラウン', 'Brown'], 'green': ['グリーン', 'Green'],
        'gray': ['グレー', 'Gray'], 'yellow': ['イエロー', 'Yellow'], 'red': ['レッド', 'Red'], 'orange': ['オレンジ', 'Orange'],
        '银色': ['シルバー', 'Silver'], 'silver': ['シルバー', 'Silver'], '金色': ['ゴールド', 'Gold'], 'gold': ['ゴールド', 'Gold'],
        '玫瑰金': ['ローズゴールド', 'Rose gold'], 'rose gold': ['ローズゴールド', 'Rose gold'], 'navy': ['ネイビー', 'Navy'],
        'french tip': ['フレンチ', 'French tip'], 'glitter': ['グリッター・ラメ', 'Glitter'], 'iridescent': ['オーロラ', 'Iridescent'],
        'chrome': ['ミラー', 'Chrome'], 'ombre': ['グラデーション', 'Ombre'], 'animal print': ['アニマル', 'Animal print'],
        'cat-eye': ['マグネット・キャッツアイ', 'Cat-eye'], 'rhinestones': ['ビジュー・ストーン', 'Rhinestones'],
        'bow': ['リボン', 'Bow'], 'hearts': ['ハート', 'Hearts'], 'butterfly': ['バタフライ', 'Butterfly'],
        'shell': ['シェル', 'Shell'], 'foil': ['箔', 'Foil'], 'fruit': ['フルーツ', 'Fruit'], 'marble': ['マーブル', 'Marble']
      };
      this.products.forEach((product) => ['colors', 'designs', 'shapes'].forEach((key) => product[key].forEach((option) => {
        option.id = String(option.id);
        const translated = labels[String(option.label).toLowerCase()];
        if (translated) option.label = translated[this.ja ? 0 : 1];
      })));
      ['colors', 'designs', 'shapes'].forEach((key) => {
        const map = new Map();
        this.products.forEach((product) => product[key].forEach((option) => { if (option.id) map.set(option.id, option); }));
        this.options[key] = Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, this.ja ? 'ja' : 'en'));
      });
      const sizes = new Set(this.products.flatMap((p) => p.variants.map((variant) => variant.size)).filter(Boolean));
      const order = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
      this.options.sizes = Array.from(sizes).sort((a, b) => {
        const ai = order.indexOf(a.toUpperCase()), bi = order.indexOf(b.toUpperCase());
        if (ai !== -1 || bi !== -1) return (ai === -1 ? 100 : ai) - (bi === -1 ? 100 : bi);
        return a.localeCompare(b, undefined, { numeric: true });
      }).map((value) => ({ id: value, label: value }));
      this.options.stock = [{ id: '1', label: this.text.available }, { id: '0', label: this.text.unavailable }];
      this.options.purposes = ['hand', 'toe'].map((id) => ({ id, label: this.text[id] }));
      // Retain deep-linked selections, including values whose products were removed.
      keys.forEach((key) => this.selected[key].forEach((value) => {
        if (!this.options[key].some((option) => option.id === value)) this.options[key].push({ id: value, label: value });
      }));
    }

    matches(product, excluded) {
      for (const key of ['colors', 'designs', 'shapes']) {
        if (key !== excluded && this.selected[key].size && !product[key].some((option) => this.selected[key].has(option.id))) return false;
      }
      const sizes = excluded === 'sizes' ? new Set() : this.selected.sizes;
      if (excluded !== 'purposes' && this.selected.purposes.size && !product.purposes.some((id) => this.selected.purposes.has(id))) return false;
      const stock = excluded === 'stock' ? new Set() : this.selected.stock;
      if (stock.size === 1 && !stock.has(product.available ? '1' : '0')) return false;
      if (sizes.size) {
        // When "in stock" and a size are chosen, that particular size must be purchasable.
        if (!product.variants.some((variant) => sizes.has(variant.size) && (!stock.has('1') || stock.has('0') || variant.available))) return false;
      }
      return true;
    }

    optionCount(key, option) {
      return this.products.filter((product) => {
        if (!this.matches(product, key)) return false;
        if (key === 'stock') return product.available === (option.id === '1');
        if (key === 'sizes') return product.variants.some((v) => v.size === option.id && (!this.selected.stock.has('1') || this.selected.stock.has('0') || v.available));
        if (key === 'purposes') return product.purposes.includes(option.id);
        return product[key].some((value) => value.id === option.id);
      }).length;
    }

    swatch(option) {
      if (option.image && /^https?:\/\//.test(option.image)) return `background-image:url('${encodeURI(option.image).replace(/'/g, '%27')}')`;
      if (/^#[0-9a-f]{3,8}$/i.test(option.color)) return `background:${option.color}`;
      const label = option.label.toLowerCase();
      const palette = [['pink|ピンク|粉', '#efb5bd'], ['red|レッド|赤|红', '#a84f4f'], ['orange|オレンジ|橙', '#e99853'], ['yellow|イエロー|黄', '#eadb75'], ['green|グリーン|緑|绿', '#7c9c83'], ['blue|ブルー|青|蓝', '#7fabc0'], ['purple|パープル|紫', '#a092b6'], ['black|ブラック|黒|黑', '#222'], ['white|ホワイト|白', '#fafafa'], ['gold|ゴールド|金', '#c7b27d'], ['silver|シルバー|銀|银|gray|グレー|灰', '#b8b8b8'], ['brown|ブラウン|茶|棕', '#967868'], ['beige|ベージュ|米', '#e6d4b7']];
      const color = palette.find(([pattern]) => new RegExp(pattern).test(label));
      if (color) return `background:${color[1]}`;
      if (/multi|マルチ|多色/.test(label)) return 'background:linear-gradient(135deg,#efb5bd,#eadb75,#7fabc0,#a092b6)';
      return 'background:linear-gradient(135deg,#fff 45%,#e9e9e9 45%,#e9e9e9 55%,#fff 55%)';
    }

    renderOptions() {
      keys.forEach((key) => {
        const container = this.aside.querySelector(`[data-nail-options="${key}"]`);
        container.innerHTML = this.options[key].map((option, index) => {
          const count = this.optionCount(key, option);
          const checked = this.selected[key].has(option.id);
          const id = `Nail-${this.section.dataset.sectionId}-${key}-${index}`;
          const label = `${option.label} (${count})`;
          return `<label class="nail-filter-option${checked ? ' is-selected' : ''}${!count && !checked ? ' is-disabled' : ''}" for="${escape(id)}" title="${escape(label)}">
            <input id="${escape(id)}" type="checkbox" name="${params[key]}" value="${escape(option.id)}" data-nail-filter-value="${key}" ${checked ? 'checked' : ''} ${!count && !checked ? 'disabled' : ''}>
            ${key === 'colors' ? `<span class="nail-swatch" style="${escape(this.swatch(option))}" aria-hidden="true"></span><span class="vna-visually-hidden">${escape(label)}</span>` : `<span>${escape(option.label)}</span><span class="nail-option-count">(${count})</span>`}
          </label>`;
        }).join('') || `<p class="nail-filter-note">${escape(this.text.noOptions)}</p>`;
      });
    }

    render(writeHistory) {
      if (!this.ready) return;
      const focusedInput = this.aside.contains(document.activeElement) ? document.activeElement?.id : null;
      const matches = this.products.filter((product) => this.matches(product));
      const pages = Math.max(1, Math.ceil(matches.length / this.pageSize));
      this.page = Math.min(this.page, pages);
      const visible = matches.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
      const chips = keys.flatMap((key) => this.options[key].filter((option) => this.selected[key].has(option.id)).map((option) => `<button class="nail-filter-chip" type="button" data-nail-remove="${key}" data-value="${escape(option.id)}" aria-label="${escape(this.text.remove + ': ' + option.label)}"><span aria-hidden="true">×</span>${escape(option.label)}</button>`));
      this.chips.innerHTML = chips.join('') + (chips.length ? `<button class="nail-filter-clear" type="button" data-nail-clear>${escape(this.text.clear)}</button>` : '');
      this.chips.hidden = !chips.length;
      if (this.triggerCount) {
        this.triggerCount.hidden = !chips.length;
        this.triggerCount.textContent = chips.length ? ` (${chips.length})` : '';
      }
      this.counter.dataset.vnaProductCount = String(matches.length);
      this.counter.textContent = `${matches.length}${this.ja ? '' : ' '}${this.text.products}`;
      this.results.innerHTML = visible.length ? `<div class="vna-product-grid">${visible.map((product) => this.cards.get(product.id)).join('')}</div>${this.pagination(pages)}` : `<p class="vna-collection-empty">${escape(this.text.empty)}</p>`;
      this.renderOptions();
      if (focusedInput) document.getElementById(focusedInput)?.focus({ preventScroll: true });
      this.aside.querySelector('[data-nail-filter-apply]').textContent = `${this.text.show} (${matches.length})`;
      if (writeHistory) window.history.pushState({}, '', this.stateUrl());
      this.section.dispatchEvent(new CustomEvent('nail:results-updated'));
    }

    stateUrl(page = this.page) {
      const url = new URL(window.location.href);
      keys.forEach((key) => {
        url.searchParams.delete(params[key]);
        this.selected[key].forEach((value) => url.searchParams.append(params[key], value));
      });
      url.searchParams.delete('page');
      url.searchParams.delete('nail_page');
      if (page > 1) url.searchParams.set('nail_page', page);
      return url;
    }

    pagination(pages) {
      if (pages <= 1) return '';
      const buttons = [];
      const add = (page, label, ariaLabel) => buttons.push(`<li><a class="vna-pagination__item${page === this.page ? ' is-current' : ''}" href="${escape(this.stateUrl(page))}" data-nail-page="${page}"${page === this.page ? ' aria-current="page"' : ''}${ariaLabel ? ` aria-label="${escape(ariaLabel)}"` : ''}>${label}</a></li>`);
      if (this.page > 1) add(this.page - 1, '‹', this.text.previous);
      const shown = Array.from({ length: pages }, (_, i) => i + 1).filter((page) => page === 1 || page === pages || Math.abs(page - this.page) <= 2);
      shown.forEach((page, index) => {
        if (index && page > shown[index - 1] + 1) buttons.push('<li><span class="vna-pagination__item">…</span></li>');
        add(page, String(page));
      });
      if (this.page < pages) add(this.page + 1, '›', this.text.next);
      return `<nav class="vna-pagination" aria-label="${this.ja ? 'ページ' : 'Pagination'}"><ul class="vna-pagination__list">${buttons.join('')}</ul></nav>`;
    }

    open() {
      this.previousFocus = document.activeElement;
      this.aside.classList.add('is-open');
      this.aside.setAttribute('role', 'dialog');
      this.aside.setAttribute('aria-modal', 'true');
      this.backdrop.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('nail-filter-open');
      this.aside.querySelector('button')?.focus();
    }

    close(restore = true) {
      const wasOpen = this.aside.classList.contains('is-open');
      this.aside.classList.remove('is-open');
      this.aside.removeAttribute('role');
      this.aside.removeAttribute('aria-modal');
      this.backdrop.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('nail-filter-open');
      if (restore && wasOpen) this.previousFocus?.focus();
    }

    destroy() { this.close(false); this.stickyObserver?.disconnect(); cancelAnimationFrame(this.stickyFrame); this.abort.abort(); }
  }

  const initialize = (root = document) => {
    root.querySelectorAll('[data-nail-series-layout]').forEach((section) => {
      if (!section.nailFilters && section.querySelector('[data-nail-filters]')) section.nailFilters = new NailFilters(section);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelector('[data-nail-series-layout]')?.nailFilters?.destroy());
})();
